/**
 * DEN FELLES KONTRAKTEN OG SIGNERINGEN (C4–C8, sak 274) over API-et.
 *
 * Én kontrakt per booking eller spillestedleie, delt av begge parter. Kontrakten har versjoner:
 * `original` (v1), `replacement` (ny versjon v2), `amendment` (tillegg T1) og `cancellation`
 * (annullering A1). Hver versjon er et utkast til den første signerer; da er den låst
 * (`locked_at`) og kan bare trekkes tilbake eller bli gjeldende. Vilkårene er avtalearkets skjema
 * (`DealSheetTerms`), låst fra den godtatte versjonen.
 *
 * Statusene regnes ut på serveren, så nettleseren og mobilen viser det samme: `displayStatus`
 * legger «Frist passert» og «Signert utenfor Tvitvi» oppå basens status.
 */
import { z } from 'zod';

import { dealTermsInputSchema, dealTermsSchema } from '../deal/deal-terms.js';
import { wireInstant } from './wire-time.js';

export const CONTRACT_STATUSES = ['none', 'draft', 'signing', 'current', 'voided'] as const;
export const contractStatusSchema = z.enum(CONTRACT_STATUSES).meta({ id: 'ContractStatus' });
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

export const CONTRACT_VERSION_KINDS = ['original', 'amendment', 'replacement', 'cancellation'] as const;
export const contractVersionKindSchema = z
  .enum(CONTRACT_VERSION_KINDS)
  .meta({ id: 'ContractVersionKind' });
export type ContractVersionKind = (typeof CONTRACT_VERSION_KINDS)[number];

export const CONTRACT_VERSION_STATUSES = [
  'draft',
  'signing',
  'current',
  'withdrawn',
  'superseded',
  'voided',
  'discarded',
] as const;
export const contractVersionStatusSchema = z
  .enum(CONTRACT_VERSION_STATUSES)
  .meta({ id: 'ContractVersionStatus' });
export type ContractVersionStatus = (typeof CONTRACT_VERSION_STATUSES)[number];

/** Det skjermen tegner: basens status, med «Frist passert» og «Signert utenfor Tvitvi» oppå. */
export const CONTRACT_DISPLAY_STATUSES = [
  'draft',
  'signing',
  'current',
  'signed_outside',
  /** En registrering «Signert utenfor Tvitvi» motparten har sagt at ikke stemmer. Gjelder ikke. */
  'disputed',
  /**
   * En kopi signert utenfor Tvitvi oppå en gjeldende kontrakt, med motparten i Tvitvi (276.16 b):
   * gjelder først når motparten har bekreftet; til da gjelder den forrige.
   */
  'awaiting_confirmation',
  'withdrawn',
  'superseded',
  'voided',
  /**
   * En annullering begge har signert (A7): den har gjort kontrakten ugyldig. «Gjeldende» om
   * annulleringsdokumentet leste som om kontrakten gjaldt.
   */
  'cancellation_signed',
  'discarded',
  'deadline_passed',
] as const;
export const contractDisplayStatusSchema = z
  .enum(CONTRACT_DISPLAY_STATUSES)
  .meta({ id: 'ContractDisplayStatus' });
export type ContractDisplayStatus = (typeof CONTRACT_DISPLAY_STATUSES)[number];

export const CONTRACT_SOURCES = ['tvitvi', 'uploaded', 'external'] as const;
export const contractSourceSchema = z.enum(CONTRACT_SOURCES).meta({ id: 'ContractSource' });
export type ContractSource = (typeof CONTRACT_SOURCES)[number];

export const CONTRACT_SIGNER_STATUSES = [
  'pending',
  'opened',
  'signed',
  'change_requested',
  'removed',
] as const;
export const contractSignerStatusSchema = z
  .enum(CONTRACT_SIGNER_STATUSES)
  .meta({ id: 'ContractSignerStatus' });
export type ContractSignerStatus = (typeof CONTRACT_SIGNER_STATUSES)[number];

/** Signeringsmetodene (`SignatureProvider`). `bankid_idura` er forberedt, ikke bygget (C2). */
export const SIGNATURE_METHODS = [
  'tvitvi_ses_session',
  'tvitvi_ses_otp_email',
  'tvitvi_ses_otp_sms',
  'bankid_idura',
] as const;
export const signatureMethodSchema = z.enum(SIGNATURE_METHODS).meta({ id: 'SignatureMethod' });
export type SignatureMethod = (typeof SIGNATURE_METHODS)[number];

export const CONTRACT_PARTIES = ['a', 'b'] as const;
export const contractPartySideSchema = z.enum(CONTRACT_PARTIES).meta({ id: 'ContractPartySide' });
export type ContractPartySide = (typeof CONTRACT_PARTIES)[number];

export const CONTRACT_SUBJECT_KINDS = ['booking', 'venue_rental'] as const;
export const contractSubjectKindSchema = z
  .enum(CONTRACT_SUBJECT_KINDS)
  .meta({ id: 'ContractSubjectKind' });
export type ContractSubjectKind = (typeof CONTRACT_SUBJECT_KINDS)[number];

export const CONTRACT_LANGUAGES = ['nb', 'en'] as const;
export const contractLanguageSchema = z.enum(CONTRACT_LANGUAGES).meta({ id: 'ContractLanguage' });

const short = z.string().trim().max(200);
const medium = z.string().trim().max(500);
const long = z.string().trim().max(20_000);

/** Én part slik den står i kontrakten (C4.2 «Partene»), hentet fra organisasjonsoppsettet. */
export const contractPartySchema = z
  .object({
    organizationId: z.uuid().nullable().optional(),
    name: short.optional(),
    legalName: short.optional(),
    orgNumber: short.optional(),
    address: medium.optional(),
    contact: z
      .object({
        personId: z.uuid().nullable().optional(),
        name: short.optional(),
        title: short.optional(),
        email: short.optional(),
        phone: short.optional(),
      })
      .optional(),
    /** Fakturamottaker, når en annen enn parten. */
    invoice: z
      .object({
        name: short.optional(),
        orgNumber: short.optional(),
        address: medium.optional(),
        email: short.optional(),
      })
      .optional(),
    /** Byråets konto for utbetaling. */
    payoutAccount: short.optional(),
    /** Artist, format og artistens org.nr. (bare på byråets side). */
    artist: z
      .object({ name: short.optional(), format: short.optional(), orgNumber: short.optional() })
      .optional(),
  })
  .meta({ id: 'ContractParty' });
export type ContractParty = z.infer<typeof contractPartySchema>;

export const contractPartiesSchema = z
  .object({ a: contractPartySchema, b: contractPartySchema })
  .meta({ id: 'ContractParties' });
export type ContractParties = z.infer<typeof contractPartiesSchema>;

export const contractExtraClauseSchema = z
  .object({ id: z.string().trim().min(1).max(60), title: short, text: long })
  .meta({ id: 'ContractExtraClause' });
export type ContractExtraClause = z.infer<typeof contractExtraClauseSchema>;

/** Teksten rundt punktene, kopiert fra organisasjonens innstillinger da kontrakten ble laget. */
export const contractClausesSchema = z
  .object({
    intro: long.optional(),
    /** Standardtekst per punkt (nøklene i `DEAL_TERM_KEYS`), vist under verdien. */
    clauseTexts: z.record(z.string(), long).optional(),
    extraClauses: z.array(contractExtraClauseSchema).max(40).optional(),
    /** Rekkefølgen: punktnøkler og id-er til tilleggspunktene. */
    order: z.array(z.string().max(60)).max(80).optional(),
    /** Praktiske punkter begge fyller inn i utkastet (C4.1). */
    practical: long.optional(),
    /** Annulleringens begrunnelse og tilleggets kommentar. */
    note: long.optional(),
  })
  .meta({ id: 'ContractClauses' });
export type ContractClauses = z.infer<typeof contractClausesSchema>;

/**
 * DE PÅKREVDE FELTENE (C4.2): «Påkrevde felt må være utfylt før kontrakten kan signeres. Manglende
 * felt vises med lenke rett dit de rettes.» `field` er stien i `ContractParty`.
 */
export const CONTRACT_REQUIRED_PARTY_FIELDS = [
  'legalName',
  'orgNumber',
  'address',
  'contact.name',
  'contact.email',
] as const;
export type ContractRequiredPartyField = (typeof CONTRACT_REQUIRED_PARTY_FIELDS)[number];

function partyValue(party: ContractParty, field: ContractRequiredPartyField): string | undefined {
  if (field === 'contact.name') return party.contact?.name;
  if (field === 'contact.email') return party.contact?.email;
  return party[field] ?? undefined;
}

/** Feltene som mangler, per part. Samme svar i nettleseren og i API-et som avviser signeringen. */
export function missingPartyFields(
  parties: ContractParties,
): { party: ContractPartySide; field: ContractRequiredPartyField }[] {
  const out: { party: ContractPartySide; field: ContractRequiredPartyField }[] = [];
  for (const side of CONTRACT_PARTIES) {
    for (const field of CONTRACT_REQUIRED_PARTY_FIELDS) {
      const value = partyValue(parties[side], field);
      if (value === undefined || value.trim() === '') out.push({ party: side, field });
    }
  }
  return out;
}

export const contractSignerDtoSchema = z
  .object({
    id: z.uuid(),
    party: contractPartySideSchema,
    personId: z.uuid().nullable(),
    name: z.string(),
    title: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    organizationName: z.string().nullable(),
    status: contractSignerStatusSchema,
    openedAt: wireInstant.nullable(),
    signedAt: wireInstant.nullable(),
    method: signatureMethodSchema.nullable(),
    changeComment: z.string().nullable(),
    /** Fullmakten slik den sto da personen signerte (C8): grensen og valutaen, null = ingen grense. */
    authorityLimitMinor: z.number().int().nullable(),
    authorityCurrency: z.string().nullable(),
    hadAuthority: z.boolean(),
    delegatedFromSignerId: z.uuid().nullable(),
    /** Personen som spør. */
    isMe: z.boolean(),
    /** Uten konto: signerer via engangslenke og kode. */
    viaLink: z.boolean(),
  })
  .meta({ id: 'ContractSigner' });
export type ContractSignerDto = z.infer<typeof contractSignerDtoSchema>;

export const contractFieldMarkSchema = z
  .object({ organizationName: z.string().nullable(), mine: z.boolean(), revision: z.number().int() })
  .meta({ id: 'ContractFieldMark' });

export const contractVersionDtoSchema = z
  .object({
    id: z.uuid(),
    kind: contractVersionKindSchema,
    label: z.string(),
    source: contractSourceSchema,
    status: contractVersionStatusSchema,
    displayStatus: contractDisplayStatusSchema,
    terms: dealTermsSchema,
    /** Punktene et tillegg eller en ny versjon endrer, mot versjonen den gjelder. */
    changedKeys: z.array(z.string()),
    parties: contractPartiesSchema,
    clauses: contractClausesSchema,
    title: z.string(),
    language: contractLanguageSchema,
    deadlineAt: wireInstant.nullable(),
    reminderAt: wireInstant.nullable(),
    revision: z.number().int(),
    /**
     * «ENDRET AV [ORG]» (C4.1): feltet og hvem som sist endret det. `unseenKeys` er det den andre
     * parten har endret siden vi sist så utkastet; de må være sett før vi kan signere.
     */
    fieldMarks: z.record(z.string(), contractFieldMarkSchema),
    unseenKeys: z.array(z.string()),
    seenByUs: z.boolean(),
    missingFields: z.array(z.object({ party: contractPartySideSchema, field: z.string() })),
    document: z
      .object({
        sha256: z.string(),
        filename: z.string().nullable(),
        bytes: z.number().int().nullable(),
        uploaded: z.boolean(),
      })
      .nullable(),
    signedDocument: z.object({ sha256: z.string() }).nullable(),
    timestamp: z.object({
      status: z.enum(['none', 'pending', 'stamped', 'failed']),
      time: z.string().nullable(),
    }),
    lockedAt: wireInstant.nullable(),
    effectiveAt: wireInstant.nullable(),
    endedAt: wireInstant.nullable(),
    replacesVersionId: z.uuid().nullable(),
    external: z
      .object({
        registeredByName: z.string().nullable(),
        registeredOrganizationName: z.string().nullable(),
        registeredByUs: z.boolean(),
        signedOn: z.iso.date().nullable(),
        confirmation: z.enum(['pending', 'confirmed', 'disputed']).nullable(),
        confirmedByName: z.string().nullable(),
        confirmedAt: wireInstant.nullable(),
        /** Merknaden fra den som registrerte. */
        comment: z.string().nullable(),
        /**
         * Kommentaren i motpartens svar («Stemmer ikke», 276.19 a). Står ved siden av merknaden, som
         * den ikke lenger skriver over; hentet fra svarhendelsen i loggen.
         */
        answerComment: z.string().nullable(),
        /** Eierens eget notat (de gamle kontraktene fra 1016). Bare eieren får det; motparten får null. */
        ownerNote: z.string().nullable(),
      })
      .nullable(),
    signers: z.array(contractSignerDtoSchema),
    createdAt: wireInstant,
  })
  .meta({ id: 'ContractVersion' });
export type ContractVersionDto = z.infer<typeof contractVersionDtoSchema>;

/** Kontraktkjeden (C9): versjonene og tilleggene, eldste først. */
export const contractChainItemSchema = z
  .object({
    id: z.uuid(),
    kind: contractVersionKindSchema,
    label: z.string(),
    source: contractSourceSchema,
    status: contractVersionStatusSchema,
    displayStatus: contractDisplayStatusSchema,
    effectiveAt: wireInstant.nullable(),
    endedAt: wireInstant.nullable(),
    createdAt: wireInstant,
  })
  .meta({ id: 'ContractChainItem' });
export type ContractChainItem = z.infer<typeof contractChainItemSchema>;

/** Statussteget i stepperen (C9). */
export const CONTRACT_STEPS = ['accepted', 'draft', 'signed_a', 'signed_b', 'current'] as const;
export type ContractStep = (typeof CONTRACT_STEPS)[number];

export const contractPermissionsSchema = z
  .object({
    canDraft: z.boolean(),
    canRegister: z.boolean(),
    canVoid: z.boolean(),
    /** Personen har fullmakt, og den dekker honoraret (C8). */
    canSign: z.boolean(),
    /**
     * «Lag ny kontrakt» etter en annullering (A9): kontrakten er annullert, ingen ny er laget, og
     * personen har den samme rettigheten som for å lage en kontrakt (`contract:draft` fra avtalearket,
     * `contract:register` for en programpost uten ark).
     */
    canRenew: z.boolean(),
    authorityLimitMinor: z.number().int().nullable(),
    hasAuthority: z.boolean(),
    authorityCoversFee: z.boolean(),
    /** Legge til og fjerne signerere for vår side: fullmakt som dekker beløpet (276.18 b). */
    canManageSigners: z.boolean(),
  })
  .meta({ id: 'ContractPermissions' });
export type ContractPermissions = z.infer<typeof contractPermissionsSchema>;

export const contractDtoSchema = z
  .object({
    id: z.uuid(),
    number: z.string(),
    title: z.string(),
    language: contractLanguageSchema,
    status: contractStatusSchema,
    subjectKind: contractSubjectKindSchema,
    /** Siden huset som spør står på. */
    ourParty: contractPartySideSchema,
    partyAName: z.string().nullable(),
    partyBName: z.string().nullable(),
    counterpartyInTvitvi: z.boolean(),
    /**
     * QA15 A2: partene slik organisasjonene i Tvitvi beskriver seg i innstillingene, for et utkast
     * (ellers null; etter første signatur er partene låst). Det som står her, er låst i utkastet: den andre parten fyller ikke
     * inn noe på en organisasjon i Tvitvi, og parten selv retter det i innstillingene.
     */
    organizationParties: z
      .object({ a: contractPartySchema.nullable(), b: contractPartySchema.nullable() })
      .nullable()
      .optional(),
    dealSheetId: z.uuid().nullable(),
    engagementId: z.uuid().nullable(),
    dateRequestId: z.uuid().nullable(),
    performanceId: z.uuid().nullable(),
    /** Versjonen som gjelder (original eller erstatning) og den som er under arbeid. */
    current: contractVersionDtoSchema.nullable(),
    open: contractVersionDtoSchema.nullable(),
    /** Gjeldende verdier: grunnversjonen med tilleggene slått sammen (C7). */
    effectiveTerms: dealTermsSchema.nullable(),
    effectiveLabel: z.string().nullable(),
    amendments: z.array(contractVersionDtoSchema),
    chain: z.array(contractChainItemSchema),
    /**
     * Resten av kjeden, så den kan åpnes (C7): erstattede og annullerte versjoner, en kopi motparten
     * sa «Stemmer ikke» til før den ble gjeldende (276.16 b), med merknaden og svaret, og annulleringen.
     */
    history: z.array(contractVersionDtoSchema),
    /** Venter på en signatur fra personen som spør, eller fra huset. */
    waitingOnMe: z.boolean(),
    waitingOnUs: z.boolean(),
    /**
     * QA16 svar 310.10: den andre parten i Tvitvi har ingen ventende signerer (`no_signer`), eller ingen
     * med fullmakt som dekker beløpet (`no_authority`). Bare et ja/nei per part, aldri hvem eller grensen.
     */
    signingGaps: z
      .array(
        z.object({
          organizationName: z.string().nullable(),
          gap: z.enum(['no_signer', 'no_authority']),
        }),
      )
      .default([]),
    permissions: contractPermissionsSchema,
    /**
     * ANNULLERINGEN (A7): når og av hvem kontrakten ble annullert, og begrunnelsen. Null når kontrakten
     * ikke er annullert. `signedBy` er signaturene på annulleringen, ikke på kontrakten.
     */
    voided: z
      .object({
        at: z.string().nullable(),
        label: z.string().nullable(),
        versionId: z.uuid().nullable(),
        note: z.string().nullable(),
        external: z.boolean(),
        signedBy: z.array(
          z.object({
            name: z.string(),
            organizationName: z.string().nullable(),
            signedAt: wireInstant.nullable(),
          }),
        ),
      })
      .nullable(),
    /** Den annullerte kontrakten denne erstatter, og den nye som erstatter denne (A9). */
    replaces: z.object({ id: z.uuid(), number: z.string() }).nullable(),
    replacedBy: z
      .object({ id: z.uuid(), number: z.string(), status: contractStatusSchema })
      .nullable(),
    /**
     * «AVVIKER FRA SIGNERT KONTRAKT» (274.2 a): scenetid og scene er avtalt, ikke låst. Endres de i
     * programmet, står avviket her, uten å sperre. Et punkt som er tomt i kontrakten, gir ingen avvik.
     */
    deviations: z.array(
      z.object({ key: z.enum(['date', 'time', 'stage']), agreed: z.string(), actual: z.string() }),
    ),
    createdAt: wireInstant,
    updatedAt: wireInstant,
  })
  .meta({ id: 'Contract' });
export type ContractDto = z.infer<typeof contractDtoSchema>;

/** Svaret når det ikke finnes en kontrakt ennå (C9 «Ingen kontrakt»): de to valgene. */
export const contractLookupSchema = z
  .object({
    contract: contractDtoSchema.nullable(),
    /** Avtalearket som er godtatt og kan bli en kontrakt, når kontrakten mangler. */
    acceptedDealSheetId: z.uuid().nullable(),
    /**
     * 302.4: AVTALEN ER GJORT UTENFOR TVITVI («Hvem opptrer?», 1186), og ingen kontrakt er lastet opp
     * ennå. `signed` når den ble ført som signert. Avtale-fanen ber da om den signerte kontrakten.
     */
    outside: z.object({ signed: z.boolean() }).nullable().default(null),
    canDraft: z.boolean(),
    canRegister: z.boolean(),
  })
  .meta({ id: 'ContractLookup' });
export type ContractLookup = z.infer<typeof contractLookupSchema>;

export const contractEventDtoSchema = z
  .object({
    seq: z.number().int(),
    kind: z.string(),
    versionLabel: z.string().nullable(),
    actorName: z.string().nullable(),
    actorOrganizationName: z.string().nullable(),
    ip: z.string().nullable(),
    at: z.string(),
    documentSha256: z.string().nullable(),
    hash: z.string(),
    prevHash: z.string().nullable(),
    payload: z.record(z.string(), z.unknown()),
  })
  .meta({ id: 'ContractEvent' });
export type ContractEventDto = z.infer<typeof contractEventDtoSchema>;

export const contractEventListSchema = z
  .object({ items: z.array(contractEventDtoSchema), chainValid: z.boolean() })
  .meta({ id: 'ContractEventList' });
export type ContractEventList = z.infer<typeof contractEventListSchema>;

/** Statusbrikken i lister (C9): én per engasjement, programpost eller datoforespørsel. */
export const contractChipSchema = z
  .object({
    contractId: z.uuid(),
    number: z.string(),
    engagementId: z.uuid().nullable(),
    performanceId: z.uuid().nullable(),
    dateRequestId: z.uuid().nullable(),
    /** Engasjementets artist i arrangørens register og hos byrået: Booking filtrerer på dem («venter på oss»). */
    organizerArtistId: z.uuid().nullable(),
    agencyArtistId: z.uuid().nullable(),
    displayStatus: contractDisplayStatusSchema,
    status: contractStatusSchema,
    waitingOnMe: z.boolean(),
    waitingOnUs: z.boolean(),
  })
  .meta({ id: 'ContractChip' });
export type ContractChip = z.infer<typeof contractChipSchema>;

export const contractChipListSchema = z
  .object({ items: z.array(contractChipSchema) })
  .meta({ id: 'ContractChipList' });
export type ContractChipList = z.infer<typeof contractChipListSchema>;

/**
 * HVOR KONTRAKTEN BOR FOR HUSET SOM SPØR (varselet på bjella): de to partene har hver sin side for den
 * samme kontrakten. `performance`: programpostens Kontrakt-fane i arrangørens hus. `agency_advance`:
 * byråets advance (den samme siden, sett fra byrået). `agency_artist`: artistens Bookinger hos byrået,
 * når advancen ikke finnes. `venue_event` (QA15-S K1): et spillesteds innslag bor på arrangementet
 * (Avtale-fanen), ikke på en programpost. `null`: ingen side, og kontrakten åpnes for seg selv.
 */
export const CONTRACT_PLACE_PAGES = [
  'performance',
  'venue_event',
  'agency_advance',
  'agency_artist',
] as const;
export const contractPlaceSchema = z
  .object({
    contractId: z.uuid(),
    page: z.enum(CONTRACT_PLACE_PAGES).nullable(),
    festivalId: z.uuid().nullable(),
    editionId: z.uuid().nullable(),
    performanceId: z.uuid().nullable(),
    artistId: z.uuid().nullable(),
    advanceId: z.uuid().nullable(),
    /** `venue_event`: the event (venue night); `festivalId` is then the venue's container. */
    nightId: z.uuid().nullable().optional(),
  })
  .meta({ id: 'ContractPlace' });
export type ContractPlace = z.infer<typeof contractPlaceSchema>;

// ------------------------------------------------------------------------------------ requests

export const createContractRequestSchema = z
  .object({ dealSheetId: z.uuid() })
  .meta({ id: 'CreateContractRequest' });
export type CreateContractRequest = z.infer<typeof createContractRequestSchema>;

/**
 * Utkastet. I en original er de kommersielle punktene låst fra det godtatte budet; `terms` tas da
 * bare imot for punkter som ikke var del av budet. I et tillegg og en ny versjon er alle punktene
 * forslaget (C7: «samme mekanikk som motbud»).
 */
export const updateContractDraftRequestSchema = z
  .object({
    revision: z.number().int().min(1),
    title: short.min(1).optional(),
    language: contractLanguageSchema.optional(),
    deadlineAt: z.iso.datetime({ offset: true }).nullable().optional(),
    reminderAt: z.iso.datetime({ offset: true }).nullable().optional(),
    parties: contractPartiesSchema.partial().optional(),
    clauses: contractClausesSchema.optional(),
    terms: dealTermsInputSchema.optional(),
  })
  .meta({ id: 'UpdateContractDraftRequest' });
export type UpdateContractDraftRequest = z.infer<typeof updateContractDraftRequestSchema>;

export const markContractSeenRequestSchema = z
  .object({ revision: z.number().int().min(1) })
  .meta({ id: 'MarkContractSeenRequest' });
export type MarkContractSeenRequest = z.infer<typeof markContractSeenRequestSchema>;

export const addContractSignerRequestSchema = z
  .object({
    party: contractPartySideSchema,
    personId: z.uuid().optional(),
    name: short.min(1).optional(),
    title: short.optional(),
    email: z.email().max(200).optional(),
    phone: z.string().trim().max(40).optional(),
  })
  .refine((b) => b.personId !== undefined || (b.name !== undefined && (b.email ?? b.phone) !== undefined), {
    error: 'signer_shape',
    path: ['personId'],
  })
  .meta({ id: 'AddContractSignerRequest' });
export type AddContractSignerRequest = z.infer<typeof addContractSignerRequestSchema>;

/** Signeringen. `documentSha256` er hashen signereren fikk vist; den må stå uendret i basen. */
export const signContractRequestSchema = z
  .object({
    signerId: z.uuid(),
    documentSha256: z.string().regex(/^[0-9a-f]{64}$/),
    /** «Jeg bekrefter at jeg har fullmakt til å signere for [org]» (C2 punkt 3, C8). */
    confirmAuthority: z.literal(true),
  })
  .meta({ id: 'SignContractRequest' });
export type SignContractRequest = z.infer<typeof signContractRequestSchema>;

/** «Send til signering hos …» (274.6 a): signereren gir plassen sin videre. */
export const delegateContractSignerRequestSchema = z
  .object({ signerId: z.uuid(), toPersonId: z.uuid() })
  .meta({ id: 'DelegateContractSignerRequest' });
export type DelegateContractSignerRequest = z.infer<typeof delegateContractSignerRequestSchema>;

export const withdrawContractRequestSchema = z
  .object({
    reason: z.enum(['withdraw', 'change_requested']),
    comment: z.string().trim().max(4000).nullable().optional(),
  })
  .meta({ id: 'WithdrawContractRequest' });
export type WithdrawContractRequest = z.infer<typeof withdrawContractRequestSchema>;

/** Tillegg, ny versjon eller annullering av en gjeldende kontrakt (C7). */
export const proposeContractChangeRequestSchema = z
  .object({
    kind: z.enum(['amendment', 'replacement', 'cancellation']),
    note: z.string().trim().max(4000).nullable().optional(),
  })
  .meta({ id: 'ProposeContractChangeRequest' });
export type ProposeContractChangeRequest = z.infer<typeof proposeContractChangeRequestSchema>;

/** En egen PDF som dokumentet i utkastet (C4.3): sendes til signering i samme flyt. */
export const uploadContractDocumentRequestSchema = z
  .object({ attachmentId: z.uuid() })
  .meta({ id: 'UploadContractDocumentRequest' });
export type UploadContractDocumentRequest = z.infer<typeof uploadContractDocumentRequestSchema>;

/**
 * «SIGNERT UTENFOR TVITVI» (C4.4). En signert kopi lastes opp, og statusen settes for hånd. Uten
 * kontrakt fra før lages den (kreves da `dealSheetId` eller programposten); med et utkast blir
 * utkastet den registrerte versjonen; med en gjeldende kontrakt blir det et tillegg eller en
 * erstatning.
 */
export const registerExternalContractRequestSchema = z
  .object({
    attachmentId: z.uuid(),
    signedOn: z.iso.date(),
    note: z.string().trim().max(4000).nullable().optional(),
    /**
     * Oppå en gjeldende kontrakt. `cancellation` bare med en motpart utenfor Tvitvi (276.24 a); med en
     * motpart i Tvitvi venter et tillegg eller en erstatning på motpartens bekreftelse (276.16 b).
     */
    kind: z.enum(['original', 'amendment', 'replacement', 'cancellation']).optional(),
  })
  .meta({ id: 'RegisterExternalContractRequest' });
export type RegisterExternalContractRequest = z.infer<typeof registerExternalContractRequestSchema>;

/** Motpartens svar på en registrering: «Bekreft» eller «Stemmer ikke» med kommentar. */
export const answerExternalContractRequestSchema = z
  .object({
    answer: z.enum(['confirmed', 'disputed']),
    comment: z.string().trim().max(4000).nullable().optional(),
  })
  .refine((b) => b.answer === 'confirmed' || (b.comment ?? '').trim() !== '', {
    error: 'comment_required',
    path: ['comment'],
  })
  .meta({ id: 'AnswerExternalContractRequest' });
export type AnswerExternalContractRequest = z.infer<typeof answerExternalContractRequestSchema>;

// ------------------------------------------------------------------- innstillinger og fullmakt

export const contractSettingsDtoSchema = z
  .object({
    intro: z.record(z.string(), z.string()),
    clauseTexts: z.record(z.string(), z.record(z.string(), z.string())),
    extraClauses: z.array(
      z.object({
        id: z.string(),
        title: z.record(z.string(), z.string()),
        text: z.record(z.string(), z.string()),
      }),
    ),
    clauseOrder: z.array(z.string()),
    payoutAccount: z.string().nullable(),
    defaultDeadlineDays: z.number().int(),
    reminderHoursBefore: z.number().int(),
    showLogo: z.boolean(),
  })
  .meta({ id: 'ContractSettings' });
export type ContractSettingsDto = z.infer<typeof contractSettingsDtoSchema>;

const localized = z.record(z.enum(['nb', 'en']), long);

export const updateContractSettingsRequestSchema = z
  .object({
    /* Innledningen og standardteksten per punkt lagres fortsatt, men et nytt utkast bruker dem ikke (QA15 A1). */
    intro: localized.optional(),
    clauseTexts: z.record(z.string().max(60), localized).optional(),
    extraClauses: z
      .array(
        z.object({
          id: z.string().trim().min(1).max(60),
          title: z.record(z.enum(['nb', 'en']), short),
          text: localized,
        }),
      )
      .max(40)
      .optional(),
    clauseOrder: z.array(z.string().max(60)).max(80).optional(),
    /** Bare med Byrå-modulen (`contract_payout_agency_only` ellers). */
    payoutAccount: short.nullable().optional(),
    defaultDeadlineDays: z.number().int().min(1).max(365).optional(),
    reminderHoursBefore: z.number().int().min(0).max(2160).optional(),
    showLogo: z.boolean().optional(),
  })
  .meta({ id: 'UpdateContractSettingsRequest' });
export type UpdateContractSettingsRequest = z.infer<typeof updateContractSettingsRequestSchema>;

export const signingAuthorityDtoSchema = z
  .object({
    id: z.uuid(),
    personId: z.uuid(),
    personName: z.string(),
    limitMinor: z.number().int().nullable(),
    currency: z.string(),
    firstSuperadmin: z.boolean(),
    createdAt: wireInstant,
  })
  .meta({ id: 'SigningAuthority' });
export type SigningAuthorityDto = z.infer<typeof signingAuthorityDtoSchema>;

export const signingAuthorityListSchema = z
  .object({ items: z.array(signingAuthorityDtoSchema) })
  .meta({ id: 'SigningAuthorityList' });
export type SigningAuthorityList = z.infer<typeof signingAuthorityListSchema>;

export const upsertSigningAuthorityRequestSchema = z
  .object({
    personId: z.uuid(),
    limitMinor: z.number().int().min(0).max(1_000_000_000_00).nullable(),
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .optional(),
  })
  .meta({ id: 'UpsertSigningAuthorityRequest' });
export type UpsertSigningAuthorityRequest = z.infer<typeof upsertSigningAuthorityRequestSchema>;

/** «Send til signering hos …»: dem i huset med høy nok fullmakt for honoraret. */
export const contractDelegateOptionSchema = z
  .object({ personId: z.uuid(), name: z.string(), limitMinor: z.number().int().nullable() })
  .meta({ id: 'ContractDelegateOption' });
export const contractDelegateOptionListSchema = z
  .object({ items: z.array(contractDelegateOptionSchema) })
  .meta({ id: 'ContractDelegateOptionList' });
export type ContractDelegateOptionList = z.infer<typeof contractDelegateOptionListSchema>;

// -------------------------------------------------------------------- den offentlige siden

export const publicSigningPageSchema = z
  .object({
    number: z.string(),
    title: z.string(),
    label: z.string(),
    kind: contractVersionKindSchema,
    language: contractLanguageSchema,
    status: z.enum(['open', 'signed', 'closed']),
    deadlineAt: wireInstant.nullable(),
    documentSha256: z.string().nullable(),
    signer: z.object({
      name: z.string(),
      title: z.string().nullable(),
      organizationName: z.string().nullable(),
      signedAt: wireInstant.nullable(),
    }),
    partyAName: z.string().nullable(),
    partyBName: z.string().nullable(),
    channels: z.array(z.enum(['email', 'sms'])),
    codeSentAt: wireInstant.nullable(),
    /** Kopien er klar når alle har signert. */
    signedDocumentReady: z.boolean(),
  })
  .meta({ id: 'PublicSigningPage' });
export type PublicSigningPage = z.infer<typeof publicSigningPageSchema>;

export const publicSendCodeRequestSchema = z
  .object({ channel: z.enum(['email', 'sms']) })
  .meta({ id: 'PublicSendCodeRequest' });
export type PublicSendCodeRequest = z.infer<typeof publicSendCodeRequestSchema>;

/** Kvitteringen. `devCode` bare når e-postdriveren er `fake` (274.5 a), aldri i drift. */
export const publicSendCodeResponseSchema = z
  .object({ sentTo: z.string(), channel: z.enum(['email', 'sms']), devCode: z.string().nullable() })
  .meta({ id: 'PublicSendCodeResponse' });
export type PublicSendCodeResponse = z.infer<typeof publicSendCodeResponseSchema>;

export const publicSignRequestSchema = z
  .object({
    code: z.string().regex(/^[0-9]{6}$/),
    documentSha256: z.string().regex(/^[0-9a-f]{64}$/),
    confirmAuthority: z.literal(true),
  })
  .meta({ id: 'PublicSignRequest' });
export type PublicSignRequest = z.infer<typeof publicSignRequestSchema>;

export const publicRequestChangeRequestSchema = z
  .object({ code: z.string().regex(/^[0-9]{6}$/), comment: z.string().trim().min(1).max(4000) })
  .meta({ id: 'PublicRequestChangeRequest' });
export type PublicRequestChangeRequest = z.infer<typeof publicRequestChangeRequestSchema>;

export const publicSigningResultSchema = z
  .object({ ok: z.literal(true), allSigned: z.boolean() })
  .meta({ id: 'PublicSigningResult' });
export type PublicSigningResult = z.infer<typeof publicSigningResultSchema>;

/** Engangskodene i Control Room, bare med `fake`-driveren (274.5 a). */
export const devSigningCodeSchema = z
  .object({ at: z.string(), number: z.string(), signerName: z.string(), sentTo: z.string(), code: z.string() })
  .meta({ id: 'DevSigningCode' });
export const devSigningCodeListSchema = z
  .object({ enabled: z.boolean(), items: z.array(devSigningCodeSchema) })
  .meta({ id: 'DevSigningCodeList' });
export type DevSigningCodeList = z.infer<typeof devSigningCodeListSchema>;
